import { useCallback, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

function gerarIdUnico() {
  return 'ntf-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
}

/**
 * Hook para gerenciar fila de notificações (toast) sequenciais.
 * Garante que notificações do mesmo "tipo" não sejam duplicadas.
 * Exibe uma por vez com intervalo configurável.
 */
export function useFilaNotificacoes({ intervaloMs = 10 * 60 * 1000, tempoToastMs = 5000 } = {}) {
  const [fila, setFila] = useState([]);
  const [exibindo, setExibindo] = useState(false);
  const timerRef = useRef();

  // Adiciona uma notificação à fila, se já não existir uma igual (por tipo)
  const pushNotification = useCallback((notificacao) => {
    setFila((filaAtual) => {
      // Se já existe uma notificação com o mesmo tipo, não adiciona
      if (
        notificacao.tipo &&
        filaAtual.some((ntf) => ntf.tipo === notificacao.tipo)
      ) {
        return filaAtual;
      }
      return [
        ...filaAtual,
        {
          ...notificacao,
          id: notificacao.id || gerarIdUnico(),
        },
      ];
    });
  }, []);

  // Remove a notificação pelo id
  const removerNotificacao = useCallback((id) => {
    setFila((filaAtual) => filaAtual.filter((ntf) => ntf.id !== id));
  }, []);

  // Efeito para exibir uma notificação por vez, em intervalos
  useEffect(() => {
    if (!exibindo && fila.length > 0) {
      setExibindo(true);
      const atual = fila[0];
      toast.info(
        <>
          <strong>{atual.titulo}</strong>
          <div>{atual.mensagem}</div>
        </>,
        {
          position: "bottom-right",
          autoClose: tempoToastMs,
          onClose: () => {
            removerNotificacao(atual.id);
            timerRef.current = setTimeout(() => {
              setExibindo(false); // Libera para próxima da fila
            }, intervaloMs);
          },
        }
      );
    }
    return () => clearTimeout(timerRef.current);
  }, [fila, exibindo, tempoToastMs, intervaloMs, removerNotificacao]);

  return { fila, pushNotification, removerNotificacao };
}